/**
 * DomainRow - Linha memoizada da tabela de domínios
 *
 * Componente otimizado com React.memo para evitar re-renders desnecessários.
 * Cada linha só re-renderiza quando seus props específicos mudam.
 */
import React, { memo } from 'react';
import {
  TableRow,
  TableCell,
  Checkbox,
  Typography,
  Chip,
  Box,
  Tooltip,
  IconButton,
  alpha
} from '@mui/material';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import {
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  AutoAwesome as AutoAwesomeIcon,
  Launch as LaunchIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import {
  getPriorityColor,
  getPriorityLabel,
  formatAudience,
  getStatusLabel,
  getStatusColor
} from '../../../utils/admin/domainUtils';

/**
 * Props:
 * - domain: objeto do domínio
 * - isSelected: boolean se está selecionado
 * - onToggleSelect: callback para seleção
 * - onVisit: callback para visitar site
 * - onFetchData: callback para buscar dados
 * - onProcess: callback para processar
 * - onIgnore: callback para ignorar
 */
const DomainRow = memo(({
  domain,
  isSelected,
  onToggleSelect,
  onVisit,
  onFetchData,
  onProcess,
  onIgnore,
  onEnrich,
  onDelete
}) => {
  const theme = useMuiTheme();
  const hasRealData = ['actual', 'partial'].includes(domain.dataQuality);

  return (
    <TableRow 
      selected={isSelected}
      hover
      sx={{
        transition: 'background-color 0.2s ease',
        '&.Mui-selected': {
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,118,255,0.05)',
          '&:hover': {
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,118,255,0.08)',
          }
        },
        '&:hover': {
          bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03) !important' : 'rgba(0,0,0,0.02) !important'
        }
      }}
    >
      <TableCell padding="checkbox">
        <Checkbox
          checked={isSelected}
          onChange={onToggleSelect}
          color="primary"
          sx={{
            color: isSelected ? theme.palette.primary.main : alpha(theme.palette.text.primary, 0.3),
            '&.Mui-checked': {
              color: theme.palette.primary.main,
            },
            transition: 'all 0.1s ease',
            '& .MuiSvgIcon-root': { fontSize: '1.3rem' } // Melhor presença na tabela
          }}
        />
      </TableCell>
      <TableCell>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{
              cursor: 'pointer',
              color: 'primary.main',
              textDecoration: 'none',
              transition: 'color 0.2s',
              '&:hover': {
                color: 'primary.dark'
              }
            }}
            onClick={onVisit}
          >
            {domain.domain}
          </Typography>

          {/* Link para HypeStat */}
          <Tooltip title="Ver estatísticas no HypeStat" arrow>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                window.open(`https://hypestat.com/info/${domain.domain}`, '_blank', 'noopener,noreferrer');
              }}
              sx={{
                padding: '2px',
                '&:hover': {
                  color: 'primary.main'
                }
              }}
            >
              <LaunchIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>

          {domain.scrapingError && (
            <Tooltip
              title={`⚠️ Site inacessível: ${domain.scrapingError.includes('SSL') ? 'Certificado SSL inválido' : domain.scrapingError.includes('ENOTFOUND') || domain.scrapingError.includes('404') ? 'Site fora do ar' : 'Erro ao acessar'}`}
              arrow
            >
              <Chip
                label="⚠️"
                size="small"
                color="error"
                sx={{
                  height: 22,
                  fontSize: '0.75rem',
                  cursor: 'help'
                }}
              />
            </Tooltip>
          )}
        </Box>
      </TableCell>
      <TableCell>
        <Typography variant="body2" fontWeight={500} color="text.primary">
          {domain.siteName || '-'}
        </Typography>
      </TableCell>
      <TableCell>
        <Chip
          label={domain.articleCount}
          size="small"
          color="primary"
        />
      </TableCell>
      <TableCell>
        {domain.estimatedAudience ? (
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title={
              hasRealData
                ? `Dados ${domain.dataQuality === 'actual' ? 'completos' : 'parciais'} do SimilarWeb. PPV: ${domain.estimatedPPV || '-'}`
                : `Valor estimado (fallback). Precisa scraping real.`
            }>
              <Typography
                variant="body2"
                color={hasRealData ? 'success.main' : 'text.secondary'}
                fontWeight={hasRealData ? 'medium' : 'normal'}
                sx={{
                  fontStyle: hasRealData ? 'normal' : 'italic',
                  opacity: hasRealData ? 1 : 0.7
                }}
              >
                {formatAudience(Math.round(domain.estimatedAudience / (365 / 12)))}
                {!hasRealData && ' *'}
              </Typography>
            </Tooltip>
            {domain.manualOverride && (
              <Chip label="Manual" size="small" color="primary" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
            )}
          </Box>
        ) : (
          <Typography variant="body2" color="text.disabled">-</Typography>
        )}
      </TableCell>
      <TableCell>
        {domain.estimatedAudience ? (
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography variant="body2">{formatAudience(domain.estimatedAudience)}</Typography>
            {domain.audienceSource && domain.audienceSource !== 'unknown' && (
              <Tooltip title={`Fonte: ${domain.audienceSource === 'hypestat' ? 'HypeStat' :
                domain.audienceSource === 'semrush' ? 'SEMrush' :
                  domain.audienceSource === 'fallback' ? 'Fallback (P10 Média)' :
                    'SimilarWeb'
                }`}>
                <Chip
                  label={
                    domain.audienceSource === 'hypestat' ? 'HS' :
                      domain.audienceSource === 'semrush' ? 'SR' :
                        domain.audienceSource === 'fallback' ? 'FB' :
                          'SW'
                  }
                  size="small"
                  color={
                    domain.audienceSource === 'fallback' ? 'warning' :
                      domain.audienceSource === 'similarweb' ? 'primary' :
                        domain.audienceSource === 'semrush' ? 'secondary' :
                          'default'
                  }
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.7rem', fontWeight: 'bold' }}
                />
              </Tooltip>
            )}
          </Box>
        ) : (
          <Typography variant="body2" color="text.disabled">-</Typography>
        )}
      </TableCell>
      <TableCell>
        {domain.cpc ? (
          <Tooltip title={`CPC (Custo Por Clique): R$ ${domain.cpc.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}>
            <Typography variant="body2" fontWeight="medium" color="info.main">
              R$ {domain.cpc.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Typography>
          </Tooltip>
        ) : (
          <Typography variant="body2" color="text.disabled">-</Typography>
        )}
      </TableCell>
      <TableCell>
        {domain.valuation ? (
          <Tooltip title={`Valoração Diária: R$ ${domain.valuation.toLocaleString('pt-BR')}/dia | Mensal: R$ ${Math.round(domain.valuation * (365 / 12)).toLocaleString('pt-BR')}/mês`}>
            <Box>
              <Typography variant="body2" fontWeight="bold" color="success.main">
                R$ {domain.valuation.toLocaleString('pt-BR')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                /dia
              </Typography>
            </Box>
          </Tooltip>
        ) : (
          <Typography variant="body2" color="text.disabled">-</Typography>
        )}
      </TableCell>
      <TableCell>
        {domain.tier ? (
          <Chip
            label={`Tier ${domain.tier}`}
            size="small"
            color={
              domain.tier === 1 ? 'error' :
                domain.tier === 2 ? 'warning' : 'info'
            }
            variant="outlined"
            sx={{ borderRadius: '6px', fontWeight: 700, borderWidth: '1.5px' }}
          />
        ) : (
          <Typography variant="body2" color="text.disabled">-</Typography>
        )}
      </TableCell>
      <TableCell>
        <Chip
          label={getPriorityLabel(domain.priority)}
          size="small"
          color={getPriorityColor(domain.priority)}
        />
      </TableCell>
      <TableCell>
        {domain.nextUpdateAt ? (
          <Tooltip title={`Frequência: ${domain.updateFrequency || 'não definida'}`}>
            <Typography
              variant="body2"
              color={
                new Date(domain.nextUpdateAt) <= new Date()
                  ? 'error.main'
                  : new Date(domain.nextUpdateAt) <= new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
                    ? 'warning.main'
                    : 'text.secondary'
              }
              fontWeight={new Date(domain.nextUpdateAt) <= new Date() ? 'bold' : 'normal'}
            >
              {new Date(domain.nextUpdateAt).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
            </Typography>
          </Tooltip>
        ) : (
          <Typography variant="body2" color="text.disabled">-</Typography>
        )}
      </TableCell>
      <TableCell>
        {new Date(domain.firstSeen).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
      </TableCell>
      <TableCell>
        <Chip
          label={getStatusLabel(domain.status, domain.dataQuality)}
          size="small"
          color={getStatusColor(domain.status, domain.dataQuality)}
        />
      </TableCell>
      <TableCell>
        {domain.enrichedAt ? (
          <Typography variant="body2" color="text.secondary">
            {new Date(domain.enrichedAt).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
            {' '}
            <Typography component="span" variant="caption" color="text.disabled">
              {new Date(domain.enrichedAt).toLocaleTimeString('pt-BR', { timeZone: 'America/Sao_Paulo', hour: '2-digit', minute: '2-digit' })}
            </Typography>
          </Typography>
        ) : (
          <Typography variant="body2" color="text.disabled">-</Typography>
        )}
      </TableCell>
      <TableCell>
        {domain.categories && domain.categories.length > 0 ? (
          <Box display="flex" flexWrap="wrap" gap={0.5}>
            {domain.categories.slice(0, 3).map((cat, idx) => (
              <Chip
                key={idx}
                label={cat}
                size="small"
                color="info"
                variant="outlined"
                sx={{ height: 22, fontSize: '0.75rem' }}
              />
            ))}
            {domain.categories.length > 3 && (
              <Tooltip title={domain.categories.join(', ')}>
                <Chip
                  label={`+${domain.categories.length - 3}`}
                  size="small"
                  color="info"
                  sx={{ height: 22, fontSize: '0.75rem' }}
                />
              </Tooltip>
            )}
          </Box>
        ) : (
          <Typography variant="body2" color="text.disabled">-</Typography>
        )}
      </TableCell>
      <TableCell>
        <Box display="flex" gap={0.5}>
          <Tooltip title="Visitar site">
            <IconButton size="small" onClick={onVisit} sx={{ color: 'text.secondary', bgcolor: 'transparent', '&:hover': { bgcolor: 'action.hover' } }}>
              <VisibilityIcon sx={{ fontSize: '1.35rem' }} />
            </IconButton>
          </Tooltip>

          <Tooltip title={domain.status === 'completed' || domain.manualOverride ? "Editar dados" : "Aprovar domínio"}>
            <IconButton 
              size="small" 
              onClick={onProcess} 
              sx={{ color: domain.status === 'completed' || domain.manualOverride ? 'primary.main' : 'success.main', bgcolor: 'transparent', '&:hover': { bgcolor: domain.status === 'completed' || domain.manualOverride ? 'primary.soft' : 'success.soft' } }}
            >
              {domain.status === 'completed' || domain.manualOverride ? <EditIcon sx={{ fontSize: '1.35rem' }} /> : <CheckCircleIcon sx={{ fontSize: '1.35rem' }} />}
            </IconButton>
          </Tooltip>

          <Tooltip title={domain.enrichedAt ? `Enriquecido em ${new Date(domain.enrichedAt).toLocaleDateString('pt-BR')}` : "Buscar dados com IA"}>
            <IconButton
              size="small"
              onClick={onEnrich}
              sx={{
                color: domain.enrichedAt ? 'success.main' : 'warning.main',
                bgcolor: 'transparent',
                opacity: domain.enrichedAt ? 0.7 : 1,
                '&:hover': { opacity: 1, bgcolor: domain.enrichedAt ? 'success.soft' : 'warning.soft' }
              }}
            >
              <AutoAwesomeIcon sx={{ fontSize: '1.35rem' }} />
            </IconButton>
          </Tooltip>

          {domain.status !== 'completed' && (
            <Tooltip title="Ignorar (Mover para rejeitados)">
              <IconButton size="small" onClick={onIgnore} sx={{ color: 'text.secondary', bgcolor: 'transparent', '&:hover': { bgcolor: 'action.hover', color: 'text.primary' } }}>
                <CancelIcon sx={{ fontSize: '1.35rem' }} />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title="Excluir permanentemente">
            <IconButton size="small" onClick={onDelete} sx={{ color: 'error.main', bgcolor: 'transparent', '&:hover': { bgcolor: 'error.soft' } }}>
              <DeleteIcon sx={{ fontSize: '1.35rem' }} />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );
}, (prevProps, nextProps) => {
  // Custom comparison: só re-renderiza se dados relevantes mudaram
  return (
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.domain._id === nextProps.domain._id &&
    prevProps.domain.status === nextProps.domain.status &&
    prevProps.domain.siteName === nextProps.domain.siteName &&
    prevProps.domain.estimatedAudience === nextProps.domain.estimatedAudience &&
    prevProps.domain.dataQuality === nextProps.domain.dataQuality &&
    prevProps.domain.tier === nextProps.domain.tier &&
    prevProps.domain.priority === nextProps.domain.priority &&
    prevProps.domain.nextUpdateAt === nextProps.domain.nextUpdateAt &&
    prevProps.domain.processedAt === nextProps.domain.processedAt &&
    prevProps.domain.enrichedAt === nextProps.domain.enrichedAt &&
    prevProps.domain.manualOverride === nextProps.domain.manualOverride &&
    prevProps.domain.scrapingError === nextProps.domain.scrapingError &&
    prevProps.domain.cpc === nextProps.domain.cpc &&
    prevProps.domain.valuation === nextProps.domain.valuation &&
    JSON.stringify(prevProps.domain.categories) === JSON.stringify(nextProps.domain.categories)
  );
});

DomainRow.displayName = 'DomainRow';

export default DomainRow;
